import { apiCall } from '@/lib/api-client';

/** v0.6.1 Fase 4 — mirror of backend household_members.relationship_type CHECK. */
export const HOUSEHOLD_RELATIONSHIP_TYPES = [
  'madre',
  'padre',
  'hermano',
  'hermana',
  'hijo',
  'hija',
  'esposo',
  'esposa',
  'primo',
  'prima',
  'tio',
  'tia',
  'sobrino',
  'sobrina',
  'abuelo',
  'abuela',
  'nieto',
  'nieta',
  'otro',
] as const;

export type HouseholdRelationshipType = typeof HOUSEHOLD_RELATIONSHIP_TYPES[number];

export interface HouseholdMemberView {
  id: string;
  sourceScreeningId: string;
  sourceClientId: string;
  sourceAgentId: string;
  sourceAgentName: string | null;
  sourceClientLastName: string | null;
  fullName: string;
  dob: string;
  dobYear: number;
  medicaidId: string | null;
  hasMedicaid: boolean | null;
  bookingId: string | null;
  status: string;
  notes: string | null;
  /** v0.6.1 Fase 4 */
  relationshipType: HouseholdRelationshipType | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHouseholdMemberInput {
  fullName: string;
  dob: string; // YYYY-MM-DD
  medicaidId?: string;
  /** v0.6.1 Fase 4 */
  relationshipType?: HouseholdRelationshipType;
}

export interface UpdateHouseholdMemberInput {
  fullName?: string;
  dob?: string;
  medicaidId?: string;
  hasMedicaid?: boolean;
  status?: 'pending' | 'contacted' | 'scheduled' | 'screened' | 'declined' | 'unreachable';
  notes?: string;
  /** v0.6.1 Fase 4 */
  relationshipType?: HouseholdRelationshipType;
}

export interface UploaderHouseholdQueueRow {
  id: string;
  sourceScreeningId: string;
  sourceAgentId: string;
  sourceAgentName: string | null;
  fullName: string;
  dobYear: number;
  hasMedicaid: boolean | null;
  hasMedicaidId: boolean;
  status: string;
  notes: string | null;
  createdAt: string;
}

export interface UploaderHouseholdQueueResponse {
  items: UploaderHouseholdQueueRow[];
  total: number;
  pendingCount: number;
}

/**
 * Cluster H v2 — one row of GET /screenings/:parentId/household-states.
 * Raw fields; deriveHouseholdScreeningState() applies the 6-state switch.
 */
export interface HouseholdStateRow {
  hmId: string;
  hmStatus: string;
  /** PHI-decrypted full name (Cluster H v2 privacy refactor). */
  fullName: string;
  declinedReason: string | null;
  declinedReasonText: string | null;
  subScreeningId: string | null;
  subIsSubmitted: boolean | null;
  subFindhelpUploadedAt: string | null;
  subFindhelpNoMedicaid: boolean | null;
  subFindhelpFlaggedError: string | null;
  lastAuditAction: string | null;
  lastAuditAt: string | null;
}

/**
 * Cluster H v2 LD-H2-13 — preset decline reasons. Server-side CHECK
 * constraint pins this to the same 5 keys (migration 027).
 */
export type HouseholdDeclineReason =
  | 'already_has_plan'
  | 'moved_out_state'
  | 'refused'
  | 'no_contact'
  | 'other';

/**
 * Cluster H v2 privacy refactor — payload of
 * GET /screenings/:parentId/household-summary.
 * NO bonus/$$$ aggregates. Members include PHI-decrypted fullName +
 * decline reasons for the ScreeningSummary card.
 */
export interface HouseholdSummaryResponse {
  parent: {
    screeningId: string;
    clientLastName: string;
    clientCinLast6: string;
    submittedAt: string;
    agentId: string;
    agentName: string | null;
  };
  members: HouseholdStateRow[];
}

export interface HouseholdStats {
  today: { captured: number; withMedicaid: number; scheduled: number };
  week: { captured: number; withMedicaid: number; scheduled: number };
  month: { captured: number; withMedicaid: number; scheduled: number };
  total: { captured: number; withMedicaid: number; scheduled: number };
  captureRateMonth: number;
  medicaidConfirmedPercent: number;
  perAgent: Array<{ agentId: string; agentName: string; captured: number; withMedicaid: number }>;
}

/**
 * Cluster H v2 privacy refactor — monthly progress for the
 * ScreeningSummary motivational card. Tier labels only (no $ amounts).
 */
export interface MonthlyProgress {
  totalThisMonth: number;
  currentTier: 'below_floor' | 'base' | 't1' | 't2' | 't3';
  nextThreshold: number | null;
  screeningsToNext: number | null;
  nextTierLabel: string | null;
}

export const householdApi = {
  listForScreening: (screeningId: string) =>
    apiCall<HouseholdMemberView[]>(`/screenings/${screeningId}/household-members`),

  capture: (screeningId: string, body: CreateHouseholdMemberInput) =>
    apiCall<HouseholdMemberView>(`/screenings/${screeningId}/household-members`, {
      method: 'POST',
      body,
    }),

  get: (id: string) =>
    apiCall<HouseholdMemberView>(`/household-members/${id}`),

  update: (id: string, body: UpdateHouseholdMemberInput) =>
    apiCall<HouseholdMemberView>(`/household-members/${id}`, {
      method: 'PATCH',
      body,
    }),

  uploaderQueue: (opts: { status?: string; limit?: number; offset?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.status) p.set('status', opts.status);
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.offset) p.set('offset', String(opts.offset));
    const qs = p.toString();
    return apiCall<UploaderHouseholdQueueResponse>(`/uploader/household-members${qs ? '?' + qs : ''}`);
  },

  adminStats: () =>
    apiCall<HouseholdStats>('/admin/household-stats'),

  // Cluster H v2 — per-household-member state for the inline panel.
  // Single query on the backend; UI calls deriveHouseholdScreeningState(row).
  listHouseholdStates: (parentScreeningId: string) =>
    apiCall<HouseholdStateRow[]>(`/screenings/${parentScreeningId}/household-states`),

  // Cluster H v2 Fase 3 — post-submit ScreeningSummary payload.
  // Privacy refactor: NO bonus/$$$ amounts. Members include
  // PHI-decrypted fullName + decline reasons.
  getHouseholdSummary: (parentScreeningId: string) =>
    apiCall<HouseholdSummaryResponse>(`/screenings/${parentScreeningId}/household-summary`),

  // Cluster H v2 privacy refactor — monthly progress motivational card.
  // Tier labels only (no DOP/USD amounts in the response).
  getMonthlyProgress: () =>
    apiCall<MonthlyProgress>(`/agents/me/monthly-progress`),

  // Cluster H v2 LD-H2-12/13 — agent confirms household member declined.
  // Terminal: backend rejects re-decline. Body required:
  //   reason: one of 5 preset keys
  //   reasonText: required when reason='other' (min 2 chars)
  decline: (
    parentScreeningId: string,
    hmId: string,
    body: { reason: HouseholdDeclineReason; reasonText?: string | null },
  ) =>
    apiCall<{ ok: true; hmId: string; status: 'declined'; declinedAt: string }>(
      `/screenings/${parentScreeningId}/household-members/${hmId}/decline`,
      { method: 'POST', body },
    ),

  // Cluster H — promote a captured household_member into a full client and
  // open an inline sub-screening. Returns the sub identifiers; the caller
  // (HouseholdScreeningPanel) feeds them into the panel's local state.
  promoteAndScreen: (
    parentScreeningId: string,
    hmId: string,
    body: { languageUsed?: string } = {},
  ) =>
    apiCall<{
      assignmentId: string;
      screeningId: string;
      clientId: string;
      subStartedAt: string;
    }>(`/screenings/${parentScreeningId}/household-members/${hmId}/promote-and-screen`, {
      method: 'POST',
      body,
    }),

  // Cluster C — hard delete a household_member. Backend wipes the row and
  // emits HOUSEHOLD_MEMBER_DELETED audit. household_members has no
  // deleted_at column; the audit_log trail is the recovery mechanism.
  delete: (id: string, reason?: string) =>
    apiCall<{ ok: true; deletedId: string }>(`/household-members/${id}`, {
      method: 'DELETE',
      body: reason ? { reason } : undefined,
    }),
};
